import {
  type FlexProps,
  ReportSection,
  ReportSectionItemContent,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { SsLedgerBudgetAndExecutionSectionProps } from '@/features/mold/models/ledger';
import {
  type SsProjectDetailedBreakdownTableDataType,
  getSsProjectDetailedBreakdownTableSource,
} from '@/features/project/review-sheet/utils/ss-project-detailed-breakdown';
import { useLocaledAmount } from '@/providers';
import { groupBy } from 'lodash-es';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CellType,
  type TableRowType,
  getEmptyTableDataByHeader,
} from '../../../utils';
import { TableContent } from './TableContent';

type TableRowElementType = TableRowType[number];

export const SsBudgetAndExecutionSection: FC<
  SsLedgerBudgetAndExecutionSectionProps
> = ({ project, segmentExpenditureAndExecution, consecutiveFiscalYears }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const projectYear = project?.fiscalYear;

  const { dataSources, pastYears } = getSsProjectDetailedBreakdownTableSource({
    data: segmentExpenditureAndExecution,
    consecutiveFiscalYears,
    projectYear,
    unit,
    isDebugMode: !!devtoolState?.isDebugMode,
  });

  const groupDataAndApplyCallback = ({
    data,
    key,
    labelRatio,
    otherRatio,
    callback,
  }: {
    data: SsProjectDetailedBreakdownTableDataType[];
    key: keyof SsProjectDetailedBreakdownTableDataType;
    labelRatio: number;
    otherRatio: number;
    callback: (
      data: SsProjectDetailedBreakdownTableDataType[],
    ) => TableRowElementType[];
  }) => {
    return Object.entries(groupBy(data, key)).map<TableRowElementType>(
      ([value, groupData]) => {
        const nullValue = value === 'null';

        const nonLevel3Target = data
          .filter((v) => !v.level3)
          .some((v) => v[key] === value);

        return {
          row: [
            {
              text: value,
              isSpace: !!nullValue,
              ratio: labelRatio,
              textWrapperStyle:
                key === 'level1'
                  ? { alignItems: 'center' }
                  : nonLevel3Target
                    ? {
                        whiteSpace: 'nowrap',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }
                    : undefined,
              cellStyle: nullValue
                ? { borderLeft: '1px solid transparent' }
                : { position: 'relative' },
            },
            {
              type: 'multiple',
              ratio: otherRatio,
              rows: callback(groupData),
            },
          ],
        };
      },
    );
  };

  // NOTE: Note that the display conditions are different from those of the projectDetailsPage
  // biome-ignore lint/correctness/useExhaustiveDependencies: 既存のDependencyListを変更した際の影響が読めないためignoreしておく。把握している人がいればignoreがなくなるよう適切に変更すること。
  const budgetBreakdownTableData = useMemo<TableRowType>(() => {
    const last3years = pastYears.flatMap((year) => {
      if (!projectYear) {
        return [];
      }
      const isLast3Year = year >= projectYear - 3 && year < projectYear;
      if (!isLast3Year) {
        return [];
      }
      return year;
    });

    const header: TableRowElementType = {
      isHeader: true,
      row: [
        { isSpace: true, cellStyle: { flex: undefined, width: 373 } },
        ...last3years.map((year) => {
          return {
            text: year,
            textWrapperStyle: { justifyContent: 'center' },
          };
        }),
        {
          text: projectYear,
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('fiscal-year-estimation', {
            year: (projectYear ?? 0) + 1,
          }),
          textWrapperStyle: { justifyContent: 'center' },
        },
      ],
    };

    const data: TableRowType = groupDataAndApplyCallback({
      data: dataSources,
      key: 'level1',
      labelRatio: 1,
      otherRatio: 14,
      callback: (level1Data) => {
        return groupDataAndApplyCallback({
          data: level1Data,
          key: 'level2',
          labelRatio: 1,
          otherRatio: 10,
          callback: (level2Data) => {
            return groupDataAndApplyCallback({
              data: level2Data,
              key: 'level3',
              labelRatio: 1,
              otherRatio: 7,
              callback: (level3Data) => {
                return groupDataAndApplyCallback({
                  data: level3Data,
                  key: 'level4',
                  labelRatio: 1.1,
                  otherRatio: 6,
                  callback: (data) => {
                    const target = data[0];
                    const textStyle: { textWrapperStyle?: FlexProps['style'] } =
                      {
                        textWrapperStyle:
                          target?.rowHeaderKey ===
                          'managementExpenseGrantOfMonetizationCriteria'
                            ? { justifyContent: 'flex-start' }
                            : { justifyContent: 'flex-end' },
                      };
                    return [
                      {
                        row: target
                          ? [
                              ...last3years.map<CellType>((v, i) => ({
                                text: target[v],
                                cellStyle:
                                  i === 1 ? { marginLeft: -1 } : undefined,
                                ...textStyle,
                              })),
                              {
                                text: target.currentYear,
                                ...textStyle,
                              },
                              { text: target.nextYear, ...textStyle },
                            ]
                          : [],
                      },
                    ];
                  },
                });
              },
            });
          },
        });
      },
    });

    return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
  }, [dataSources, pastYears, projectYear, t]);

  return (
    <ReportSection
      title={t('budget-and-execution')}
      items={[
        {
          subtitle: `${t('budget-execution-table')}\n${t('report-amount-unit-description')}`,
          item: <TableContent noneBorder data={budgetBreakdownTableData} />,
        },
        {
          subtitle: t('other-special-notes'),
          isLongText: true,
          item: (
            <ReportSectionItemContent
              value={project?.otherSpecialNotes}
              isLast
            />
          ),
        },
      ]}
    />
  );
};
